import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
// AgentDisabledBanner — pinned above the agent detail scroll area whenever the
// owner has switched the agent off, so the state is visible from any tab and
// the way back on is always one click away.
//
// Enable failures render here rather than in a toast: the banner is on screen
// for the whole time the agent is disabled, and a refusal the owner has to act
// on outlives a message that disappears.
import type { AgentLifecycle } from '../hooks/useAgentLifecycle';
import { DISABLED_LABEL } from '../lib/agent-state';

interface Props {
  agentName: string;
  lifecycle: AgentLifecycle;
}

export default function AgentDisabledBanner({ agentName, lifecycle }: Props) {
  const { i18n } = useLingui();
  if (!lifecycle.disabled) return null;
  const enabling = lifecycle.pending === 'enable';

  return (
    <>
      <div className="agent-disabled-banner" role="status" data-testid="agent-disabled-banner">
        <span className="agent-state-chip agent-state-chip-disabled">{i18n._(DISABLED_LABEL)}</span>
        <span className="agent-disabled-banner-text">
          <Trans>
            {agentName} is not receiving messages. Everything it already said and produced stays
            readable.
          </Trans>
        </span>
        <button
          className="btn btn-sm btn-primary"
          data-testid="agent-enable-button"
          onClick={() => {
            void lifecycle.enable();
          }}
          disabled={lifecycle.pending !== null}
        >
          {enabling ? t(i18n)`Enabling…` : t(i18n)`Enable`}
        </button>
      </div>
      {lifecycle.enableError && (
        <div className="agent-disabled-banner-error">
          <div className="agent-inline-error" role="alert" data-testid="agent-enable-failed-error">
            <strong>
              <Trans>Can't enable {agentName}.</Trans>
            </strong>{' '}
            {i18n._(lifecycle.enableError)}
            <div className="agent-inline-error-actions">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  void lifecycle.enable();
                }}
                disabled={lifecycle.pending !== null}
              >
                {enabling ? t(i18n)`Enabling…` : t(i18n)`Try again`}
              </button>
              <button className="btn btn-sm" onClick={lifecycle.dismissEnableError}>
                <Trans>Dismiss</Trans>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
